import { niceScale, formatNumber, DEFAULT_COLORS, measureText, roundRect } from './utils.js';
import { renderers } from '../charts/index.js';

const DPR = () => (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);

export default class SimpleChart {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.getElementById(container) : container;
    if (!this.container) throw new Error('SimpleChart: container not found');

    this.options = Object.assign(
      {
        type: 'line',
        title: '',
        series: [],
        colors: DEFAULT_COLORS,
        showLegend: true,
        showGrid: true,
        animate: true,
        animationDuration: 600,
        backgroundColor: '#ffffff',
        axisX: {},
        axisY: {},
      },
      options
    );

    // Normalize: allow either `data` (single series shorthand) or `series`
    if (this.options.data && !this.options.series.length) {
      this.options.series = [{ name: this.options.name || 'Series 1', data: this.options.data }];
    }

    this._buildDom();
    this._bindEvents();
    this._progress = this.options.animate ? 0 : 1;
    this._explodeIndex = -1;
    this._explodeAmount = 0;
    this.render();
  }

  _buildDom() {
    this.container.style.position = this.container.style.position || 'relative';
    this.canvas = document.createElement('canvas');
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.display = 'block';
    this.container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    this.tooltip = document.createElement('div');
    Object.assign(this.tooltip.style, {
      position: 'absolute',
      pointerEvents: 'none',
      background: 'rgba(0,0,0,0.8)',
      color: '#fff',
      padding: '6px 10px',
      borderRadius: '4px',
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      opacity: '0',
      transition: 'opacity 0.1s',
      whiteSpace: 'nowrap',
      zIndex: 10,
    });
    this.container.appendChild(this.tooltip);
  }

  _bindEvents() {
    this._resizeObserver = new ResizeObserver(() => this._resize());
    this._resizeObserver.observe(this.container);
    this._resize();

    this.canvas.addEventListener('mousemove', (e) => this._onMouseMove(e));
    this.canvas.addEventListener('mouseleave', () => this._hideTooltip());
    this.canvas.addEventListener('click', (e) => this._onClick(e));
  }

  _resize() {
    const rect = this.container.getBoundingClientRect();
    const dpr = DPR();
    this.width = Math.max(rect.width, 100);
    this.height = Math.max(rect.height || 300, 150);
    this.canvas.width = this.width * dpr;
    this.canvas.height = this.height * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.render();
  }

  // ---- layout -------------------------------------------------------
  _computeLayout() {
    const ctx = this.ctx;
    const padding = { top: 20, right: 20, bottom: 30, left: 40 };

    if (this.options.title) {
      const titleFontSize = typeof this.options.title === 'object' ? (this.options.title.fontSize || 16) : 16;
      padding.top += titleFontSize + 12;
    }

    const isCartesian = ['line', 'bar', 'area', 'scatter', 'stackedBar'].includes(this.options.type);

    if (isCartesian) {
      const allValues = this.options.series.flatMap((s) => s.data.map((d) => (typeof d === 'object' ? d.y ?? d.value : d)));
      const min = Math.min(0, ...allValues);
      const max = Math.max(...allValues);
      this._scaleY = niceScale(min, max);

      let maxLabelW = 0;
      this._scaleY.ticks.forEach((t) => {
        const w = measureText(ctx, formatNumber(t), '11px Arial');
        if (w > maxLabelW) maxLabelW = w;
      });
      padding.left = Math.max(padding.left, maxLabelW + 16);

      if (this.options.axisX && this.options.axisX.labelAngle) {
        padding.bottom += 30;
      }
    }

    const isSliceChartForLegend = ['pie', 'doughnut'].includes(this.options.type);
    const legendItemCountForPadding = isSliceChartForLegend ? (this.options.series[0]?.data.length || 0) : this.options.series.length;
    if (this.options.showLegend !== false && legendItemCountForPadding > (isSliceChartForLegend ? 0 : 1)) {
      padding.bottom += legendItemCountForPadding > 4 ? 48 : 24;
    }

    this._padding = padding;
    this._plot = {
      x: padding.left,
      y: padding.top,
      w: Math.max(this.width - padding.left - padding.right, 10),
      h: Math.max(this.height - padding.top - padding.bottom, 10),
    };
  }

  // ---- drawing --------------------------------------------------------
  render() {
    if (!this.ctx) return;
    this._computeLayout();
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.width, this.height);

    if (this.options.backgroundColor) {
      ctx.fillStyle = this.options.backgroundColor;
      ctx.fillRect(0, 0, this.width, this.height);
    }

    if (this.options.title) {
      const t = this.options.title;
      const isObj = typeof t === 'object';
      const text = isObj ? t.text : t;
      const fontSize = isObj ? (t.fontSize || 16) : 16;
      const fontFamily = isObj ? (t.fontFamily || 'Arial') : 'Arial';
      const fontWeight = isObj ? (t.fontWeight || 'bold') : 'bold';
      const color = isObj ? (t.color || '#333') : '#333';
      const align = isObj ? (t.align || 'center') : 'center'; // 'left' | 'center' | 'right'

      ctx.fillStyle = color;
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
      ctx.textAlign = align;
      ctx.textBaseline = 'top';
      const titleX = align === 'left' ? this._padding.left : align === 'right' ? this.width - this._padding.right : this.width / 2;
      ctx.fillText(text, titleX, 10);
    }

    const renderer = renderers[this.options.type];
    if (!renderer) {
      throw new Error(`SimpleChart: unknown chart type "${this.options.type}"`);
    }
    this._hitAreas = renderer.draw(this, ctx, this._progress);

    const isSliceChart = ['pie', 'doughnut'].includes(this.options.type);
    const legendItemCount = isSliceChart ? (this.options.series[0]?.data.length || 0) : this.options.series.length;
    if (this.options.showLegend !== false && legendItemCount > (isSliceChart ? 0 : 1)) {
      this._drawLegend();
    }

    if (this.options.animate && this._progress < 1) {
      this._progress = Math.min(1, this._progress + 1000 / 60 / this.options.animationDuration);
      requestAnimationFrame(() => this.render());
    }
  }

  _drawLegend() {
    const ctx = this.ctx;
    const isSliceChart = ['pie', 'doughnut'].includes(this.options.type);
    let items;
    if (isSliceChart) {
      const s = this.options.series[0];
      items = (s?.data || []).map((d, i) => ({
        label: typeof d === 'object' ? d.x ?? d.label ?? `Slice ${i + 1}` : `Slice ${i + 1}`,
        color: (typeof d === 'object' && d.color) || this.options.colors[i % this.options.colors.length],
      }));
    } else {
      items = this.options.series.map((s, i) => ({
        label: s.name || `Series ${i + 1}`,
        color: s.color || this.options.colors[i % this.options.colors.length],
      }));
    }

    ctx.font = '12px Arial';
    const itemWidths = items.map((it) => 18 + measureText(ctx, it.label, '12px Arial') + 20);
    const maxRowW = this.width - 20;

    // wrap items into rows that fit the chart width
    const rows = [[]];
    let rowW = 0;
    items.forEach((it, i) => {
      const iw = itemWidths[i];
      if (rowW + iw > maxRowW && rows[rows.length - 1].length) {
        rows.push([]);
        rowW = 0;
      }
      rows[rows.length - 1].push({ ...it, w: iw });
      rowW += iw;
    });

    const rowHeight = 20;
    const startY = this.height - this._padding.bottom + 12;

    rows.forEach((row, rowIndex) => {
      const totalW = row.reduce((sum, it) => sum + it.w, 0);
      let x = this.width / 2 - totalW / 2;
      const y = startY + rowIndex * rowHeight;
      row.forEach((it) => {
        ctx.fillStyle = it.color;
        roundRect(ctx, x, y, 12, 12, 2);
        ctx.fill();
        ctx.fillStyle = '#333';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(it.label, x + 18, y + 6);
        x += it.w;
      });
    });
  }

  // ---- interaction ----------------------------------------------------
  _findHit(mx, my) {
    if (!this._hitAreas || !this._hitAreas.length) return null;
    let hit = null;
    let bestDist = Infinity;
    for (const area of this._hitAreas) {
      if (area.contains(mx, my)) {
        const d = area.distance ? area.distance(mx, my) : 0;
        if (d < bestDist) {
          bestDist = d;
          hit = area;
        }
      }
    }
    return hit;
  }

  _onClick(e) {
    const rect = this.canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const hit = this._findHit(mx, my);

    if (hit && hit.raw && typeof this.options.onClick === 'function') {
      this.options.onClick(hit.raw, e);
    }

    if (['pie', 'doughnut'].includes(this.options.type) && hit && hit.raw) {
      const idx = hit.raw.dataIndex;
      this._explodeIndex = this._explodeIndex === idx ? -1 : idx;
      this._animateExplode();
    }
  }

  _animateExplode() {
    const target = this._explodeIndex === -1 ? 0 : 1;
    const step = () => {
      const diff = target - this._explodeAmount;
      if (Math.abs(diff) < 0.02) {
        this._explodeAmount = target;
        this.render();
        return;
      }
      this._explodeAmount += diff * 0.25;
      this.render();
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  _onMouseMove(e) {
    const rect = this.canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const hit = this._findHit(mx, my);

    if (hit) {
      this.tooltip.innerHTML = hit.label;
      this.tooltip.style.left = `${hit.tooltipX ?? mx}px`;
      this.tooltip.style.top = `${(hit.tooltipY ?? my) - 36}px`;
      this.tooltip.style.opacity = '1';
      this.canvas.style.cursor = 'pointer';
    } else {
      this._hideTooltip();
    }
  }

  _hideTooltip() {
    this.tooltip.style.opacity = '0';
    this.canvas.style.cursor = 'default';
  }

  // ---- public API -------------------------------------------------------
  updateOptions(newOptions) {
    Object.assign(this.options, newOptions);
    if (this.options.data && newOptions.data) {
      this.options.series = [{ name: this.options.name || 'Series 1', data: this.options.data }];
    }
    this._explodeIndex = -1;
    this._explodeAmount = 0;
    this._progress = this.options.animate ? 0 : 1;
    this.render();
  }

  destroy() {
    this._resizeObserver.disconnect();
    this.container.removeChild(this.canvas);
    this.container.removeChild(this.tooltip);
  }
}
